import React from 'react';
import moment from 'moment';
import { FiClock, FiMoreVertical } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import usePermission from '@/hooks/usePermission';
import { cn } from '@/lib/utils';

// Mesmo mapeamento de badge por status usado no modal de detalhe
// (LiveClassDetailModal) — aqui também define o fundo destacado do card
// "ao vivo agora", espelhando o card semanal do aluno (Playful/LiveClassCard).
export const STATUS_META = {
  'ao-vivo': { label: 'Ao vivo', badgeVariant: 'red' },
  agendada: { label: 'Agendada', badgeVariant: 'info-soft' },
  finalizada: { label: 'Finalizada', badgeVariant: 'muted' }
};

function formatTimeRange(liveClass) {
  const start = moment(liveClass['initial-date']);
  const end = moment(liveClass['end-date']);
  const isSameDay = start.isSame(end, 'day');
  return `${start.format(isSameDay ? 'H[h]mm' : 'DD/MM - H[h]mm')} às ${end.format('H[h]mm')}`;
}

export function LiveClassCardTeacher({ liveClass, status, onClick, onEdit, onDelete }) {
  const meta = STATUS_META[status];

  // Mesmas permissões do menu (⋯) do card no Default (LiveClassCard.jsx) —
  // menu fica fora do <button> do card (não dentro) pra não aninhar
  // <button> dentro de <button>, já que o card inteiro também é clicável.
  const canEdit = usePermission('Editar aulas ao vivo', 'Aulas ao vivo');
  const canDelete = usePermission('Excluir aulas ao vivo', 'Aulas ao vivo');
  const showActionsMenu = canEdit || canDelete;

  return (
    <div
      className="relative"
      data-testid="live-class-card-teacher-wrapper"
      data-title={liveClass.title}
    >
      <button
        type="button"
        onClick={onClick}
        className={cn('flex w-full flex-col gap-2 rounded-2xl border border-neutral-lightest p-3 text-left shadow-card transition-shadow duration-150 hover:shadow-card-hover', status === 'ao-vivo' ? 'bg-[#FFF5CC]' : 'bg-white')}
        data-testid="live-class-card-teacher"
        data-title={liveClass.title}
      >
        <Badge
          variant={meta.badgeVariant}
          className="text-2xs w-fit rounded-[4px] px-1 font-extrabold uppercase leading-none tracking-wide"
        >
          {meta.label}
        </Badge>

        <div>
          <h4
            className="mb-0 truncate pr-6 text-sm font-extrabold text-neutral-900"
            title={liveClass.title}
          >
            {liveClass.title}
          </h4>
          {liveClass.courses?.[0]?.name && <p className="mb-0 truncate text-sm text-neutral-dark">{liveClass.courses[0].name}</p>}
        </div>

        <p className="mb-0 flex items-center gap-1 text-xs font-semibold text-neutral-dark">
          <FiClock className="h-3.5 w-3.5 shrink-0" />
          {formatTimeRange(liveClass)}
        </p>
      </button>

      {showActionsMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Mais opções"
              className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-neutral-dark hover:bg-black/5"
            >
              <FiMoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            {canEdit && <DropdownMenuItem onSelect={() => onEdit(liveClass)}>Editar</DropdownMenuItem>}
            {canDelete && <DropdownMenuItem onSelect={() => onDelete(liveClass)}>Excluir</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
